if (!customElements.get('mp-media-gallery')) {
  customElements.define('mp-media-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.listen = (el, type, fn, options = {}) => el?.addEventListener(type, fn, {...options, signal: this.controller.signal});
      this.track = this.querySelector('.mp-gallery__track');
      this.slides = [...this.querySelectorAll('.mp-gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-index]')];
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.mp-lightbox__stage');
      this.image = this.querySelector('[data-lightbox-image]');
      this.status = this.querySelector('[data-lightbox-status]');
      this.images = this.slides.map((slide, index) => ({slide, index, opener: slide.querySelector('[data-full-src]')})).filter(item => item.opener);
      this.current = 0;
      this.pointers = new Map();
      this.scale = 1;
      this.x = this.y = 0;
      this.listen(this.querySelector('.mp-gallery__prev'), 'click', () => this.go(this.current - 1));
      this.listen(this.querySelector('.mp-gallery__next'), 'click', () => this.go(this.current + 1));
      this.thumbs.forEach((button, i) => this.listen(button, 'click', () => this.go(i)));
      this.images.forEach(item => this.listen(item.opener, 'click', () => this.open(item)));
      this.listen(this.track, 'scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.mark(Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth))));
      }, {passive:true});
      this.listen(this.track, 'keydown', event => {
        if (event.target.closest('video, iframe, model-viewer')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.go(this.current + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.listen(this.querySelector('[data-lightbox-close]'), 'click', () => this.dialog.close());
      this.listen(this.dialog, 'close', () => {
        document.documentElement.style.overflow = this.oldOverflow;
        this.pointers.clear(); this.go(this.current, false);
        this.returnFocus?.focus({preventScroll:true});
      });
      this.listen(this.querySelector('[data-lightbox-prev]'), 'click', () => this.change(-1));
      this.listen(this.querySelector('[data-lightbox-next]'), 'click', () => this.change(1));
      this.listen(this.querySelector('[data-zoom-in]'), 'click', () => this.zoom(this.scale * 1.5));
      this.listen(this.querySelector('[data-zoom-out]'), 'click', () => this.zoom(this.scale / 1.5));
      this.listen(this.querySelector('[data-zoom-reset]'), 'click', () => this.reset());
      this.listen(this.dialog, 'keydown', event => {
        if (['ArrowLeft','ArrowRight','+','=','-','0'].includes(event.key)) event.preventDefault();
        if (event.key === 'ArrowLeft') this.change(-1);
        if (event.key === 'ArrowRight') this.change(1);
        if (event.key === '+' || event.key === '=') this.zoom(this.scale * 1.5);
        if (event.key === '-') this.zoom(this.scale / 1.5);
        if (event.key === '0') this.reset();
      });
      this.listen(this.image, 'load', () => { this.status.textContent = ''; this.transform(); });
      this.listen(this.image, 'error', () => { this.status.textContent = this.status.dataset.error; });
      this.listen(this.stage, 'wheel', event => {
        event.preventDefault(); this.zoom(this.scale * Math.exp(-event.deltaY * .002));
      }, {passive:false});
      this.listen(this.stage, 'dblclick', () => this.scale > 1 ? this.reset() : this.zoom(2));
      this.listen(this.stage, 'pointerdown', event => {
        if (event.button !== 0) return;
        this.stage.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, {x:event.clientX,y:event.clientY});
        this.start = {x:event.clientX,y:event.clientY};
        this.wasPinch = this.pointers.size > 1;
        if (this.pointers.size === 2) this.pinch = this.distance();
        this.stage.classList.add('is-dragging');
      });
      this.listen(this.stage, 'pointermove', event => {
        const previous = this.pointers.get(event.pointerId);
        if (!previous) return;
        this.pointers.set(event.pointerId, {x:event.clientX,y:event.clientY});
        if (this.pointers.size === 2) {
          const distance = this.distance();
          if (this.pinch > 0) this.zoom(this.scale * distance / this.pinch);
          this.pinch = distance; this.wasPinch = true;
        } else if (this.scale > 1) {
          this.x += event.clientX - previous.x; this.y += event.clientY - previous.y; this.transform();
        }
      });
      const release = event => {
        if (!this.pointers.has(event.pointerId)) return;
        if (event.type === 'pointerup' && this.pointers.size === 1 && this.scale === 1 && !this.wasPinch) {
          const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.change(dx < 0 ? 1 : -1);
        }
        this.pointers.delete(event.pointerId);
        if (!this.pointers.size) this.stage.classList.remove('is-dragging');
      };
      this.listen(this.stage, 'pointerup', release);
      this.listen(this.stage, 'pointercancel', release);
      this.listen(window, 'resize', () => { this.go(this.current, false); if (this.dialog.open) this.transform(); });
      const section = this.closest('.shopify-section') || document;
      this.listen(section, 'mp:variant:change', event => {
        const mediaId = String(event.detail?.featured_media?.id || '');
        const index = this.slides.findIndex(slide => slide.dataset.mediaId === mediaId);
        if (index >= 0) {
          this.go(index, false);
          if (this.dialog.open && this.slides[index].dataset.mediaType === 'image') this.show(this.images.findIndex(item => item.index === index));
        }
      });
      const initial = this.slides.findIndex(slide => slide.dataset.mediaId === this.dataset.initialMedia);
      this.initialFrame = requestAnimationFrame(() => this.go(Math.max(0, initial), false));
    }
    disconnectedCallback() {
      if (this.dialog?.open) { this.dialog.close(); document.documentElement.style.overflow = this.oldOverflow; }
      this.controller?.abort(); this.controller = null;
      cancelAnimationFrame(this.initialFrame); cancelAnimationFrame(this.scrollFrame);
    }
    mark(index) {
      this.current = Math.max(0, Math.min(index, this.slides.length - 1));
      this.thumbs.forEach((button,i) => button.setAttribute('aria-pressed', String(i === this.current)));
      const counter = this.querySelector('.mp-gallery__counter');
      counter.textContent = this.slides.length ? `${this.current + 1} / ${this.slides.length}` : '';
      this.slides.forEach((slide,i) => {
        slide.inert = i !== this.current;
        if(i !== this.current) slide.querySelector('video')?.pause();
      });
    }
    go(index, smooth = true) {
      this.mark(index);
      const behavior = smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant';
      this.track.scrollTo({left:this.current * this.track.clientWidth,behavior});
      const thumb = this.thumbs[this.current];
      if (thumb) thumb.parentElement.scrollTo({left:thumb.offsetLeft - thumb.parentElement.offsetLeft - thumb.parentElement.clientWidth / 2 + thumb.clientWidth / 2,behavior});
    }
    open(item) {
      this.returnFocus = item.opener;
      this.oldOverflow = document.documentElement.style.overflow;
      this.dialog.showModal(); document.documentElement.style.overflow = 'hidden';
      this.show(this.images.indexOf(item));
      this.querySelector('[data-lightbox-close]').focus();
    }
    show(index) {
      if (!this.images.length) return;
      this.imageIndex = (index + this.images.length) % this.images.length;
      const item = this.images[this.imageIndex];
      this.current = item.index;
      this.reset(); this.status.textContent = this.status.dataset.loading;
      this.image.alt = item.opener.querySelector('img').alt;
      this.image.src = item.opener.dataset.fullSrc;
      this.querySelector('[data-image-counter]').textContent = `${this.imageIndex + 1} / ${this.images.length}`;
      this.querySelector('[data-lightbox-prev]').disabled = this.images.length < 2;
      this.querySelector('[data-lightbox-next]').disabled = this.images.length < 2;
      this.returnFocus = item.opener;
      this.go(item.index, false);
      if (this.image.complete && this.image.naturalWidth) this.status.textContent = '';
    }
    change(delta) { this.show(this.imageIndex + delta); }
    distance() { const [a,b] = [...this.pointers.values()]; return Math.hypot(a.x-b.x,a.y-b.y); }
    zoom(scale) { this.scale = Math.max(1, Math.min(6, scale)); this.transform(); }
    reset() { this.scale = 1; this.x = this.y = 0; this.transform(); }
    transform() {
      const maxX = Math.max(0, (this.image.clientWidth * this.scale - this.stage.clientWidth) / 2);
      const maxY = Math.max(0, (this.image.clientHeight * this.scale - this.stage.clientHeight) / 2);
      this.x = Math.max(-maxX, Math.min(maxX, this.x)); this.y = Math.max(-maxY, Math.min(maxY, this.y));
      this.image.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
      this.stage.classList.toggle('is-zoomed', this.scale > 1);
      this.querySelector('[data-zoom-reset]').textContent = `${Math.round(this.scale * 100)}%`;
      this.querySelector('[data-zoom-out]').disabled = this.scale <= 1;
      this.querySelector('[data-zoom-in]').disabled = this.scale >= 6;
    }
  });
}
